import {
  mainnet,
  optimism,
  arbitrum,
  base,
  celo,
  worldchain,
  tempo,
  polygon,
  bsc,
  hyperEvm,
  robinhood,
  linea,
  monad,
  megaeth,
  scroll,
  zora,
  arc,
} from 'viem/chains'

import { SupportedChainId } from '~/constants/chains'
import { IAddress, TokenSendRule } from '~/types'
import { Address, EvmAddress } from '~/utils/address'

export const ADDRESS_ZERO =
  '0x0000000000000000000000000000000000000000' as EvmAddress.Type
export const WAREHOUSE_NATIVE_TOKEN =
  '0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE'

export const REVERSE_RECORDS_ADDRESS =
  '0x3671aE578E63FdF66ad4F3E12CC0c0d71Ac7510C'
export const MAKER_ERC20_ADDRESS = '0x9f8F72aA9304c8B593d555F12eF6589cC3A579A2'
export const RETH_ERC20_ADDRESS = '0xae78736Cd615f374D3085123A210448E74Fc6393'
export const CBETH_ERC20_ADDRESS = '0xBe9895146f7AF43049ca1c1AE358B0541Ea49704'

export const BASE_USDC_ADDRESS = '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913'

export const AUTO_EARN_V1_MODULE_ADDRESS = EvmAddress.format(
  '0x204DDc725f37786072a4D4102CBF3945F3597D13',
)
export const AUTO_EARN_V2_MODULE_ADDRESS = EvmAddress.format(
  '0x4A5aCfc49597D1D326221cd159d42817918B9F5f',
)
export const AUTO_EARN_MAINNET_V2_MODULE_ADDRESS = EvmAddress.format(
  '0x2E59620f3521E52aC6CBa4EFd556e6C750b35C9a',
)
export const WORKSPACE_AUTO_EARN_CHAIN_IDS = [base.id, mainnet.id] as const
export type WorkspaceAutoEarnChainId =
  (typeof WORKSPACE_AUTO_EARN_CHAIN_IDS)[number]
export const WORKSPACE_AUTO_EARN_ENROLLER_ADDRESSES: Record<
  WorkspaceAutoEarnChainId,
  Address.Type
> = {
  [base.id]: EvmAddress.format('0xcb74048F89B6cADA4C6C7903662BDC465e6A7De6'),
  [mainnet.id]: EvmAddress.format('0xB574ECA4Bb912B5d71E6B1f90950225908E27201'),
}
export const OPTIMISM_USDC_ADDRESS =
  '0x0b2C639c533813f4Aa9D7837CAf62653d097Ff85'
const ARBITRUM_USDC_ADDRESS = '0xaf88d065e77c8cC2239327C5EDb3A432268e5831'
export const ETHEREUM_USDC_ADDRESS =
  '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48'
const CELO_USDC_ADDRESS = '0xcebA9300f2b948710d2653dD7B07f33A8B32118C'
const WORLDCHAIN_USDC_ADDRESS = '0x79A02482A880bCE3F13e09Da970dC34db4CD24d1'
const WORLDCHAIN_WLD_ADDRESS = '0x2cFc85d8E48F8EAB294be644d9E25C3030863003'
export const TEMPO_USD_ADDRESS = '0x20C0000000000000000000000000000000000000'
export const TEMPO_BRIDGED_USDC_ADDRESS =
  '0x20C000000000000000000000b9537d11c60E8b50'

const POLYGON_USDC_ADDRESS = '0x3c499c542cEF5E3811e1192ce70d8cC03d5c3359'
export const POLYGON_USDT_ADDRESS = '0xc2132D05D31c914a87C6611C10748AEb04B58e8F'

const BSC_USDC_ADDRESS = '0x8AC76a51cc950d9822D68b83fE1Ad97B32Cd580d'
export const BSC_USDT_ADDRESS = '0x55d398326f99059fF775485246999027B3197955'

const HYPEREVM_USDC_ADDRESS = '0xb88339CB7199b77E23DB6E890353E22632Ba630f'
export const HYPEREVM_USDT0_ADDRESS =
  '0xB8CE59FC3717ada4C02eaDF9682A9e934F625ebb'

// Paxos Global Dollar, the native stablecoin on Robinhood Chain (no USDC there)
const ROBINHOOD_USDG_ADDRESS = '0x5fc5360D0400a0Fd4f2af552ADD042D716F1d168'

const LINEA_USDC_ADDRESS = '0x176211869cA2b568f2A7D4EE941E073a821EE1ff'
const MONAD_USDC_ADDRESS = '0x754704Bc059F8C67012fEd69BC8A327a5aafb603'
const MEGAETH_WETH_ADDRESS = '0x4200000000000000000000000000000000000006'
export const MEGAETH_USDT0_ADDRESS =
  '0xB8CE59FC3717ada4C02eaDF9682A9e934F625ebb'
export const MEGAETH_USDM_ADDRESS = '0xFAfDdbb3FC7688494971a79cc65DCa3EF82079E7'
// Canonical bridged USDC — no native Circle USDC on these two
const SCROLL_USDC_ADDRESS = '0x06eFdBFf2a14a7c8E15944D1F4A48F9F95F663A4'
const ZORA_USDZC_ADDRESS = '0xCccCCccc7021b32EBb4e8C08314bD62F7c653EC4'

export const BASE_USDT_ADDRESS = '0xfde4C96c8593536E31F229EA8f37b2ADa2699bb2'
export const BASE_WBTC_ADDRESS = '0x0555E30da8f98308EdB960aa94C0Db47230d2B9c'
export const BASE_CBBTC_ADDRESS = '0xcbB7C0000aB88B473b1f5aFd9ef808440eed33Bf'
export const BASE_WSTETH_ADDRESS = '0xc1CBa3fCea344f92D9239c08C0568f6F2F0ee452'
const MAINNET_EURC_ADDRESS = '0x1aBaEA1f7C830bD89Acc67eC4af516284b1bC33c'
export const BASE_EURC_ADDRESS = '0x60a3E35Cc302bFA44Cb288Bc5a4F316Fdb1adb42'

export const OPTIMISM_WBTC_ADDRESS =
  '0x68f180fcCe6836688e9084f035309E29Bf0A2095'

export const POPULAR_TOKENS: { [chainId in SupportedChainId]: IAddress[] } = {
  [mainnet.id]: [
    ADDRESS_ZERO,
    '0x6B175474E89094C44Da98b954EedeAC495271d0F', // DAI
    ETHEREUM_USDC_ADDRESS, // USDC
    '0xdAC17F958D2ee523a2206206994597C13D831ec7', // USDT
    '0x2260FAC5E5542a773Aa44fBCfeDf7C193bc2C599', // WBTC
  ],
  [optimism.id]: [
    ADDRESS_ZERO,
    '0x4200000000000000000000000000000000000042', // OP
    '0xDA10009cBd5D07dd0CeCc66161FC93D7c9000da1', // DAI
    OPTIMISM_USDC_ADDRESS, // USDC
    '0x4200000000000000000000000000000000000006', // ETH
    '0x1F32b1c2345538c0c6f582fCB022739c4A194Ebb', // WSTETH,
    '0x94b008aA00579c1307B0EF2c499aD98a8ce58e58', // USDT
    OPTIMISM_WBTC_ADDRESS, // WBTC
  ],
  [arbitrum.id]: [
    ADDRESS_ZERO,
    '0xDA10009cBd5D07dd0CeCc66161FC93D7c9000da1', // DAI
    ARBITRUM_USDC_ADDRESS, // USDC
    '0xFd086bC7CD5C481DCC9C85ebE478A1C0b69FCbb9', // USDT
    '0x2f2a2543B76A4166549F7aaB2e75Bef0aefC5B0f', // WBTC
  ],
  [base.id]: [
    ADDRESS_ZERO,
    '0x4200000000000000000000000000000000000006', // WETH
    BASE_USDC_ADDRESS,
    BASE_EURC_ADDRESS, // EURC
    '0x50c5725949A6F0c72E6C4a641F24049A917DB0Cb', // DAI,
    '0x0578d8A44db98B23BF096A382e016e29a5Ce0ffe', // HIGHER,
    '0x4ed4E862860beD51a9570b96d89aF5E1B0Efefed', // DEGEN,
    BASE_USDT_ADDRESS, // USDT
    BASE_WBTC_ADDRESS, // WBTC
    BASE_CBBTC_ADDRESS, // cbBTC
    BASE_WSTETH_ADDRESS, // WSTETH
  ],
  [celo.id]: [ADDRESS_ZERO, CELO_USDC_ADDRESS],
  [worldchain.id]: [
    ADDRESS_ZERO,
    WORLDCHAIN_USDC_ADDRESS,
    WORLDCHAIN_WLD_ADDRESS,
  ],
  [tempo.id]: [TEMPO_USD_ADDRESS, TEMPO_BRIDGED_USDC_ADDRESS],
  [polygon.id]: [
    ADDRESS_ZERO,
    '0x7ceB23fD6bC0adD59E62ac25578270cFf1b9f619', // WETH
    POLYGON_USDC_ADDRESS,
    POLYGON_USDT_ADDRESS,
  ],
  [bsc.id]: [
    ADDRESS_ZERO,
    '0xbb4CdB9CBd36B01bD1cBaEBF2De08d9173bc095c', // WBNB
    '0x4DB5a66E937A9F4473fA95b1cAF1d1E1D62E29EA', // WETH
    BSC_USDC_ADDRESS,
    BSC_USDT_ADDRESS,
  ],
  [hyperEvm.id]: [
    ADDRESS_ZERO,
    '0x5555555555555555555555555555555555555555', // WHYPE
    HYPEREVM_USDC_ADDRESS,
    HYPEREVM_USDT0_ADDRESS,
  ],
  [robinhood.id]: [ADDRESS_ZERO, ROBINHOOD_USDG_ADDRESS],
  [linea.id]: [ADDRESS_ZERO, LINEA_USDC_ADDRESS],
  [monad.id]: [ADDRESS_ZERO, MONAD_USDC_ADDRESS],
  [megaeth.id]: [
    ADDRESS_ZERO,
    MEGAETH_WETH_ADDRESS,
    MEGAETH_USDT0_ADDRESS,
    MEGAETH_USDM_ADDRESS,
  ],
  [scroll.id]: [ADDRESS_ZERO, SCROLL_USDC_ADDRESS],
  [zora.id]: [ADDRESS_ZERO, ZORA_USDZC_ADDRESS],
  // Native USDC only: the 0x3600… ERC-20 is the same balance
  [arc.id]: [ADDRESS_ZERO],
}

// Keep in sync with USDC_TOKENS in the backend splits repo
export const USDC_TOKENS: { [chainId in SupportedChainId]: IAddress } = {
  [mainnet.id]: ETHEREUM_USDC_ADDRESS,
  [base.id]: BASE_USDC_ADDRESS,
  [optimism.id]: OPTIMISM_USDC_ADDRESS,
  [arbitrum.id]: ARBITRUM_USDC_ADDRESS,
  [celo.id]: CELO_USDC_ADDRESS,
  [worldchain.id]: WORLDCHAIN_USDC_ADDRESS,
  [polygon.id]: POLYGON_USDC_ADDRESS,
  [bsc.id]: BSC_USDC_ADDRESS,
  [hyperEvm.id]: HYPEREVM_USDC_ADDRESS,
  [tempo.id]: TEMPO_BRIDGED_USDC_ADDRESS,
}

export const EURC_TOKENS: Partial<{ [chainId in SupportedChainId]: IAddress }> =
  {
    [mainnet.id]: MAINNET_EURC_ADDRESS,
    [base.id]: BASE_EURC_ADDRESS,
  }

/**
 * Returns the Bridge token type give a chain ID and token address.
 *
 * Returns null if Bridge doesn't support the provided pairing.
 */
export const getBridgeTokenType = (
  chainId: SupportedChainId,
  tokenAddress: Address.Type,
): 'usdc' | 'eurc' | null => {
  if (chainId === base.id) {
    if (Address.equal(tokenAddress, USDC_TOKENS[base.id])) return 'usdc'
    if (Address.equal(tokenAddress, EURC_TOKENS[base.id])) return 'eurc'
  }

  if (chainId === mainnet.id) {
    if (Address.equal(tokenAddress, USDC_TOKENS[mainnet.id])) return 'usdc'
    if (Address.equal(tokenAddress, EURC_TOKENS[mainnet.id])) return 'eurc'
  }

  if (chainId === tempo.id) {
    if (Address.equal(tokenAddress, USDC_TOKENS[tempo.id])) return 'usdc'
  }

  return null
}

// Stripe/Bridge processing fee for offramps (bank transfers)
// Keep in sync with DEVELOPER_FEE_PERCENT in backend splits repo
// Display: `${BRIDGE_FEE_PERCENT}%` → "0.25%"
// Rate for calculations: BRIDGE_FEE_PERCENT / 100 → 0.0025
export const BRIDGE_FEE_PERCENT = 0.25

// Bridge's actual minimum is $1 for most currencies (https://apidocs.bridge.xyz/docs/transaction-costs)
// We use $10/€10 to be extra cautious
export const MINIMUM_OFFRAMP_AMOUNT = 10

// Splits-owned Base account that gas balance top-ups pay into. Keep in sync
// with SPLITS_GAS_RECIPIENT in the backend splits repo; the server derives a
// top-up from Base USDC sent here and labels the address the same way.
export const SPLITS_GAS_RECIPIENT = EvmAddress.format(
  '0x51610851367D65D1c473916ffCC4D7b2D35c9685',
)

// The send form applies this to the gas recipient the way it applies a
// contact's send rules, so the picker offers nothing the server won't credit.
export const SPLITS_GAS_SEND_RULES: TokenSendRule[] = [
  { chainId: base.id, tokenAddress: EvmAddress.format(BASE_USDC_ADDRESS) },
]

// Splits' names for its own addresses; resolveDisplayName decides where they rank.
export const SPLITS_ADDRESS_LABELS: Partial<Record<IAddress, string>> = {
  [SPLITS_GAS_RECIPIENT]: 'Splits gas',
}

export const DEFAULT_GAS_TOP_UP_AMOUNT_USD = 25
export const MINIMUM_GAS_TOP_UP_AMOUNT_USD = 5
